import { useState, type ReactElement } from 'react'
import { isEmpty, isNotEmpty, type UniversalSearchItem, type UserId } from '@terros/common'
import { Text, TextInput } from '@mantine/core'
import { useDebounce, useToastError } from '@hooks'
import { useTranslation, useUniversalSearchQuery, type DashboardAudienceTarget } from '@front/model'
import { UserInfo } from '@components'
import { AudienceOptionList, AudienceOptionRow } from './AudienceOptionList'

type Props = {
  selectedTarget: DashboardAudienceTarget | undefined
  onSelect: (target: DashboardAudienceTarget) => void
}

export function AudiencePersonOptions({ selectedTarget, onSelect }: Props): ReactElement {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search.trim(), 500)
  const {
    data: results = [],
    isLoading,
    error,
  } = useUniversalSearchQuery(
    { query: debouncedSearch, scopes: ['User'], archived: false },
    isNotEmpty(debouncedSearch)
  )
  useToastError(error)
  const people = results.map(toPerson).filter(isNotEmpty)
  const query = search.trim()

  return (
    <>
      <TextInput
        placeholder={t('Search users')}
        value={search}
        onChange={(event) => setSearch(event.currentTarget.value)}
      />
      <AudienceOptionList>
        {people.map((person) => (
          <AudienceOptionRow
            isSelected={selectedTarget?.entityId === person.userId}
            key={person.userId}
            onSelect={() => onSelect({ entityId: person.userId, name: person.name })}
          >
            <UserInfo linked={false} size='sm' userId={person.userId} />
          </AudienceOptionRow>
        ))}
        {isEmpty(query) && <Text c='dimmed'>{t('Search for a user')}</Text>}
        {isNotEmpty(query) && isLoading && <Text c='dimmed'>{t('Searching...')}</Text>}
        {isNotEmpty(query) && !isLoading && isEmpty(people) && <Text c='dimmed'>{t('No results found')}</Text>}
      </AudienceOptionList>
    </>
  )
}

type Person = {
  userId: UserId
  name: string
}

function toPerson(item: UniversalSearchItem): Person | undefined {
  if (item.type === 'User') return { userId: item.userId, name: item.description }
  return undefined
}
